import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../common/state/store";
import { useForm } from "react-hook-form";
import AccessTypeTable from "../../features/projects/components/AccessTypeTable";
import {
  getNewProjectGroup,
  INewProjectGroup,
  postProjectGroup,
  setCurrentElements,
  setNewProjectGroup,
  setSelectedProjectGroup,
  fetchAllCurrentElements,
  setSelectedProjectGroupFromProjectGroupId,
  fetchSpecificProjectGroupWithProjects,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch } from "../../common/state/hooks";
import { getCurrentOrganization, getCurrentUser } from "../../features/profile/state/profileSlice";
import { FooterButtons } from "../../common/components/FooterButtons";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import {
  duplicateProject,
  createAndAssignProjectFromAiGraph,
} from "../../features/projects/state/projectsSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { LoadingState, setIsLoading } from "src/common/state/slice/appStateSlice";
import {
  getLastAiModuleContext,
  resetLastAiModuleContext,
  setIdOfLatestProjectGroupCursedByAi,
} from "../../features/chatbot/state/chatbotSlice";
interface ITemplateProjectAccess {
  decrementStep(): void;
}

interface LocationState {
  cloneProject?: boolean;
  cloneAiModule?: boolean;
}

export const PROJECT_GROUP_CREATE = "projectGroupCreate";

const TemplateProjectAccess = ({ decrementStep }: ITemplateProjectAccess) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation() as { state?: LocationState };
  const currentUser = useSelector(getCurrentUser);
  const currentOrg = useSelector(getCurrentOrganization);
  const lastClonedProjectId = useSelector((state: RootState) => state.projects.lastClonedProjectId);
  const lastClonedProjectName = useSelector(
    (state: RootState) => state.projects.lastClonedProjectName,
  );
  const newProjectGroup = useSelector(getNewProjectGroup);
  const { handleSubmit, setValue, watch } = useForm<INewProjectGroup>();
  const cloneProject = location.state?.cloneProject || false;
  const cloneAiModule = location.state?.cloneAiModule || false;
  const lastAiModuleContext = useSelector(getLastAiModuleContext);

  const onSubmit = async (data: INewProjectGroup) => {
    if (!currentOrg) return;

    dispatch(
      postProjectGroup({
        name: newProjectGroup.newName,
        source_thumbnail: newProjectGroup.thumbnailId,
        organization: Number(currentOrg.id),
        auth_type: data?.accessType ? data.accessType : "classic",
        componentId: PROJECT_GROUP_CREATE,
        theme_info: newProjectGroup.themeId,
        created_by: currentUser?.email ? currentUser.email : "",
        source_app_main_menu_media: Number(newProjectGroup.menuMediaId),
      }),
    )
      .then((res: any) => {
        const projectGroupId = res?.payload?.data?.id;

        dispatch(setSelectedProjectGroup(res.payload.data));

        if (cloneProject && lastClonedProjectId) {
          dispatch(
            duplicateProject({
              project: lastClonedProjectId,
              thumbnail: 47,
              projectGroup: projectGroupId,
              newName: lastClonedProjectName,
              enabledLanguagesIds: ["1"],
              componentId: PROJECT_GROUP_CREATE,
            }),
          )
            .then(async () => {
              dispatch(
                setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Loading }),
              );
              // Fetch updated project-group data with individual error handling
              const results = await Promise.allSettled([
                dispatch(setSelectedProjectGroupFromProjectGroupId(projectGroupId)),
                dispatch(fetchSpecificProjectGroupWithProjects({ projectGroupId })),
                dispatch(fetchAllCurrentElements({ projectGroupId })),
              ]);

              // Log individual failures without blocking navigation
              results.forEach((result, index) => {
                if (result.status === "rejected") {
                  const operationNames = [
                    "setSelectedProjectGroupFromProjectGroupId",
                    "fetchSpecificProjectGroupWithProjects",
                    "fetchAllCurrentElements",
                  ];
                  console.error(`Failed to execute ${operationNames[index]}:`, result.reason);
                }
              });
              dispatch(
                setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Success }),
              );

              navigate(`/courses/${projectGroupId}/list`);

              dispatch(
                setAlert({
                  msg: t(
                    "chooseTemplateType.createCourseFromTemplateModel.projectGeneratedSuccessfully",
                  ),
                  type: "success",
                  isOpen: true,
                  alertShowCloseButton: true,
                  customTimeoutInMs: 10000,
                }),
              );
            })
            .catch((error) => {
              console.error("Error while cloning the project", error);
              navigate("/");
              dispatch(
                setAlert({
                  msg: t("error.errorWhileCreatingProject"),
                  type: "error",
                  isOpen: true,
                  alertShowCloseButton: true,
                }),
              );
            });
        } else if (
          cloneAiModule &&
          lastAiModuleContext.conversationId !== null &&
          lastAiModuleContext.outputIndex !== null
        ) {
          // Handle AI module cloning into the new project-group
          dispatch(
            createAndAssignProjectFromAiGraph({
              conversationId: lastAiModuleContext.conversationId,
              relatedGptOutputIndex: lastAiModuleContext.outputIndex,
              generateBlockadeImages: lastAiModuleContext.generateBlockadeImages,
              assignToProjectGroup: projectGroupId,
              componentId: PROJECT_GROUP_CREATE,
            }),
          )
            .then(async () => {
              dispatch(
                setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Loading }),
              );
              // Fetch updated project-group data with individual error handling
              const results = await Promise.allSettled([
                dispatch(setSelectedProjectGroupFromProjectGroupId(projectGroupId)),
                dispatch(fetchSpecificProjectGroupWithProjects({ projectGroupId })),
                dispatch(fetchAllCurrentElements({ projectGroupId })),
              ]);

              // Log individual failures without blocking navigation
              results.forEach((result, index) => {
                if (result.status === "rejected") {
                  const operationNames = [
                    "setSelectedProjectGroupFromProjectGroupId",
                    "fetchSpecificProjectGroupWithProjects",
                    "fetchAllCurrentElements",
                  ];
                  console.error(`Failed to execute ${operationNames[index]}:`, result.reason);
                }
              });

              dispatch(
                setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Success }),
              );

              // Mark as AI-generated
              dispatch(setIdOfLatestProjectGroupCursedByAi(projectGroupId));

              // Navigate to PG list
              navigate(`/courses/${projectGroupId}/list`);

              // Show success alert
              dispatch(
                setAlert({
                  msg: t("pages.chat.createProjectFromAiGraph.projectGroupGeneratedSuccessfully"),
                  type: "success",
                  isOpen: true,
                  alertShowCloseButton: true,
                  customTimeoutInMs: 10000,
                }),
              );
            })
            .catch((error) => {
              console.error("Error while cloning AI module:", error);

              // Reset loading state to prevent UI stuck in loading
              dispatch(
                setIsLoading({ componentId: PROJECT_GROUP_CREATE, loading: LoadingState.Idle }),
              );

              // Navigate to PG even if cloning failed (PG was created successfully)
              // This differs from template cloning error handling (navigates to "/")
              // because the project-group exists and should be accessible to the user
              navigate(`/courses/${projectGroupId}/list`);

              dispatch(
                setAlert({
                  msg: t("error.errorWhileCreatingProject"),
                  type: "error",
                  isOpen: true,
                  alertShowCloseButton: true,
                }),
              );
            })
            .finally(() => {
              // Always reset AI context after use
              dispatch(resetLastAiModuleContext());
            });
        } else {
          navigate(`/courses/${projectGroupId}/list`);
        }

        dispatch(
          setNewProjectGroup({
            accessType: "classic",
            newName: "",
            thumbnailId: 47,
            themeId: 1,
            menuMediaId: 5346,
          }),
        );

        dispatch(setCurrentElements([]));
      })
      .catch((error) => {
        console.error("Error in validateAndSubmit:", error);
        navigate("/");
        dispatch(
          setAlert({
            msg: t("error.errorWhileCreatingProject"),
            type: "error",
            isOpen: true,
            alertShowCloseButton: true,
          }),
        );
      });
  };

  return (
    <>
      <IsLoading componentId={PROJECT_GROUP_CREATE} showSuccess={false} spinnerPlaceholder>
        <h1 className="text-2xl font-medium w-full max-w-7xl mx-auto mt-6 mb-4 px-6 xl:px-3">
          {t("chooseTemplateType.accessType")} :
        </h1>
        <form
          className="flex flex-col grow w-full h-full justify-between items-center overflow-y-auto personalize-scroll-visible"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="px-6 xl:px-3">
            <AccessTypeTable setValue={setValue} watch={watch} />
          </div>

          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={() => navigate("/courses/")}
            backText={t("general.back")}
            backCb={() => decrementStep()}
            nextText={t("general.create")}
          />
        </form>
      </IsLoading>
    </>
  );
};

export default TemplateProjectAccess;
